import React, { useEffect } from 'react';
import classnames from 'classnames';
import { Badge, Spinner } from 'react-bootstrap';
import { connect } from 'react-redux';
import {
  NavLink,
  Route,
  RouteComponentProps,
  Switch,
  withRouter,
} from 'react-router-dom';
import { DataVendor, FetchSearchesParams } from 'v2/models';
import { QueryOptions } from 'v2/queries/types';
import angleIcon from '../../icons/angle.svg';
import searchIcon from '../../icons/search.svg';
import detailsIcon from '../../icons/details.svg';
import listIcon from '../../icons/list.svg';
import DataVendorDetail from './DataVendorDetail';
import styles from './dataVendorDetailsContainer.module.scss';
import { useQuery, UseQueryResult } from 'react-query';
import DataProductsContainer from './DataProductsTab/DataProductsContainer';
import RequestsContainer, {
  buildRequestsParams,
} from './RequestsTab/RequestsContainer';
import { fetchSearches } from 'v2/hooks/useFetchSearchesPaginated';
import useHistoryBackState from 'v2/hooks/useHistoryBackState';
import { TranslationKeys, PostProcessKeys } from 'translations';
import { useTranslation } from 'react-i18next';
import InvalidDRMLinkMessage from 'v2/pages/DrmFeatures/Shared/InvalidDRMLinkMessage/InvalidDRMLinkMessage';
import { useDataVendor } from 'v2/queries/useDataVendor';

const useRequestsCountQuery = (
  dataVendor: DataVendor | undefined,
  queryOptions: QueryOptions<number> = {}
): UseQueryResult<number> => {
  return useQuery(
    [
      'Query amount of requests within master organizaiton where the provider accepted the proposal',
      dataVendor?.data_provider,
    ],
    async () => {
      if (dataVendor === undefined || dataVendor.data_provider === null) {
        return 0;
      }

      const data = await fetchSearches({
        ...buildRequestsParams(dataVendor),
        size: 1,
      } as FetchSearchesParams);

      return data.count;
    },
    queryOptions
  );
};

export type DataVendorRouterProps = RouteComponentProps<{ id: string }>;

export const DataVendorDetailsContainer = ({
  match,
}: DataVendorRouterProps) => {
  const { t } = useTranslation();
  const { path, url, params } = match;
  const {
    backLinkTitle: backLinkTitleFromState,
    counterToGoBack,
    goBackHistory,
  } = useHistoryBackState('/data-providers/1');
  const backLinkTitle =
    backLinkTitleFromState ||
    t(TranslationKeys.dataProvider_other, {
      postProcess: PostProcessKeys.TitleCase,
    });

  const {
    data: dataVendor,
    refetch: refetchDataVendor,
    isError: isDataVendorError,
  } = useDataVendor({
    id: Number(params.id),
    refetchOnWindowFocus: false,
    cacheTime: 0,
    retry: false,
  });

  const {
    data: requestsCount,
    refetch: fetchRequestsCount,
    isFetching: isRequestsCountFetching,
  } = useRequestsCountQuery(dataVendor, {
    enabled: false,
  });

  useEffect(() => {
    if (dataVendor) fetchRequestsCount();
  }, [dataVendor?.id]);

  if (isDataVendorError) {
    return <InvalidDRMLinkMessage />;
  }

  if (!dataVendor || isRequestsCountFetching) {
    return (
      <div className="loadingIndicatorContainer">
        <Spinner role="progressbar" animation="border" />
      </div>
    );
  }

  const LINKS = [
    {
      to: `${url}/detail`,
      label: 'Details',
      icon: detailsIcon,
    },
    {
      to: `${url}/data-sets`,
      label: t(TranslationKeys.dataProduct_other, {
        postProcess: PostProcessKeys.TitleCase,
      }),
      icon: listIcon,
    },
  ];

  if (requestsCount && requestsCount > 0) {
    LINKS.push({
      to: `${url}/requests`,
      label: 'Requests',
      icon: searchIcon,
    });
  }

  return (
    <div className={classnames('bg-white h-100')}>
      <div
        className={classnames(
          'd-flex',
          'justify-content-between',
          'bg-light',
          'position-relative',
          'z-index-1',
          'page-title'
        )}
      >
        <div className="container-xl d-flex justify-content-between position-relative fix-bg-images">
          <div
            className={classnames(
              'align-items-center text-truncate align-self-center',
              styles.header
            )}
          >
            <div
              onClick={() => goBackHistory()}
              className={classnames(styles.prevLink, 'text-truncate')}
              data-cy="datavendors:backlink-to-datavendors-list-page"
            >
              <img src={angleIcon} alt="angleIcon"></img>
              <h1
                className={classnames('m-0 text-truncate pr-1')}
                data-testid="go-back-title"
                data-cy={`datavendor:${backLinkTitle.replaceAll(' ', '-')}`}
              >
                {backLinkTitle}
              </h1>
            </div>
            <div
              className={classnames('text-truncate', styles.title)}
              data-testid="header_name"
              data-cy={`datavendor:backlink-display-title`}
            >
              {dataVendor.name}
            </div>
          </div>
          <div
            className={classnames(styles.pills, 'd-flex align-items-center')}
          >
            {LINKS.map((link, index) => {
              return (
                <NavLink
                  key={index}
                  to={{
                    pathname: link.to,
                    state: {
                      counterToGoBack: counterToGoBack - 1,
                      backLinkTitle,
                    },
                  }}
                  aria-label={`Switch to ${link.label} tab`}
                  className="subNavLink"
                >
                  <Badge
                    pill
                    className="mr-2 mr-lg-5 d-flex align-items-center"
                    data-cy={`datavendor:${link.label}-badge`}
                  >
                    <img src={link.icon} className="mr-2" alt="navIcon"></img>
                    {link.label}
                  </Badge>
                </NavLink>
              );
            })}
          </div>
        </div>
      </div>
      <div
        className={classnames(
          'bg-white h-100 px-4',
          styles.routeDetailContainer
        )}
      >
        <Switch>
          <Route path={`${path}/detail`}>
            <DataVendorDetail
              dataVendor={dataVendor}
              refetch={refetchDataVendor}
            />
          </Route>
          <Route path={`${path}/data-sets`}>
            <DataProductsContainer
              dataVendor={dataVendor}
              refetchDataVendor={refetchDataVendor}
            />
          </Route>
          <Route path={`${path}/requests`}>
            <RequestsContainer dataVendor={dataVendor} />
          </Route>
        </Switch>
      </div>
    </div>
  );
};

export default connect<DataVendorRouterProps>(null)(
  withRouter(DataVendorDetailsContainer)
);
